import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, RefreshCw, Check, X, Ban, Plus, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import type { ManagedCampaignData } from "@/components/managed-campaign/ManagedCampaignData";
import { useAudienceContext } from "@/hooks/useAudienceContext";
import { COUNTRIES, CITIES_BY_COUNTRY, INDUSTRIES, COMPANY_SIZES, SENIORITY, DEPARTMENTS, JOB_TITLES_BY_DEPARTMENT, ALL_JOB_TITLES } from "@/lib/audienceOptions";

type Key =
  | "countries" | "cities" | "industries" | "companySizes" | "seniorityLevels" | "departments"
  | "jobTitles" | "companyKeywords" | "personKeywords" | "excludeCompanyKeywords" | "excludePersonKeywords";

const GROUPS: Array<[Key, string]> = [
  ["countries", "Countries"], ["cities", "Cities"], ["industries", "Industries"], ["companySizes", "Company size"],
  ["seniorityLevels", "Seniority"], ["departments", "Departments"], ["jobTitles", "Job titles"],
  ["companyKeywords", "Company keywords"], ["personKeywords", "Person keywords"],
];
const EXCL: Array<[Key, string]> = [["excludeCompanyKeywords", "Exclude company keywords"], ["excludePersonKeywords", "Exclude person keywords"]];
const ALL_KEYS = [...GROUPS, ...EXCL].map(([k]) => k);

interface Props {
  data: ManagedCampaignData;
  updateData: (d: Partial<ManagedCampaignData>) => void;
  companyProfileId?: string;
  campaignKnowledgeId?: string;
  guestToken?: string;
  workspaceId?: string | null;
  onAiApplied?: () => void;
  /** Only the AI build/rebuild row; filters are edited in the tabs. */
  compact?: boolean;
}

async function call(body: Record<string, unknown>) {
  const { data, error } = await supabase.functions.invoke("company-profile-builder", { body });
  if (error) {
    let msg = error.message;
    try { const ctx = await (error as any).context?.json?.(); if (ctx?.error) msg = ctx.error; } catch { /* ignore */ }
    throw new Error(msg);
  }
  if (data?.error) throw new Error(data.error);
  return data;
}

interface GroupCfg {
  options: string[];
  allowCustom: boolean;
  labelFor?: (v: string) => string;
  /** Short fixed lists show everything on open; long lists wait for typing. */
  showOnOpen?: boolean;
  /** Values that must never be suggested (e.g. exclusions). */
  blocked?: string[];
  /** Message shown instead of a box when the group can't be filled yet. */
  disabledHint?: string;
  /** One-tap ideas shown under thin groups. */
  quick?: string[];
}

function rank(options: string[], q: string, lbl: (v: string) => string, skip: Set<string>, limit = 8) {
  const out: Array<[number, string]> = [];
  for (const o of options) {
    if (skip.has(o.toLowerCase())) continue;
    const l = lbl(o).toLowerCase();
    let s = -1;
    if (!q) s = 3;
    else if (l.startsWith(q)) s = 0;
    else if (l.split(/[\s/&(),-]+/).some((w) => w.startsWith(q))) s = 1;
    else if (l.includes(q)) s = 2;
    if (s >= 0) out.push([s, o]);
  }
  return out.sort((a, b) => a[0] - b[0]).slice(0, limit).map(([, o]) => o);
}

function ChipGroup({ label, values, onRemove, onAdd, danger, cfg }: { label: string; values: string[]; onRemove: (v: string) => void; onAdd: (v: string) => void; danger?: boolean; cfg: GroupCfg }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [hi, setHi] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const lbl = cfg.labelFor ?? ((v: string) => v);
  const skip = new Set([...values, ...(cfg.blocked ?? [])].map((v) => v.toLowerCase()));
  const q = query.trim().toLowerCase();
  const list = q || cfg.showOnOpen ? rank(cfg.options, q, lbl, skip) : [];
  const exact = cfg.options.find((o) => lbl(o).toLowerCase() === q);
  const canCustom = cfg.allowCustom && !!q && !exact && !skip.has(q);
  const items: Array<{ v: string; custom?: boolean }> = [...list.map((v) => ({ v })), ...(canCustom ? [{ v: query.trim(), custom: true }] : [])];

  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);
  useEffect(() => { setHi(0); }, [query]);

  const close = () => { setOpen(false); setQuery(""); };
  const pick = (v: string) => { onAdd(v); setQuery(""); inputRef.current?.focus(); };
  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, items.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (items[hi]) pick(items[hi].v);
      else if (exact && !skip.has(exact.toLowerCase())) pick(exact);
    } else if (e.key === "Escape") close();
    else if (e.key === "Backspace" && !query && values.length) onRemove(values[values.length - 1]);
  };

  return (
    <div className="space-y-2">
      <div className={cn("flex items-center gap-1 text-xs font-semibold uppercase tracking-wide", danger ? "text-destructive" : "text-muted-foreground")}>
        {danger && <Ban className="h-3 w-3" />}{label}{values.length > 0 && ` · ${values.length}`}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {values.map((v) => (
          <span key={v} className={cn(
            "inline-flex items-center gap-1 rounded-full py-1 pl-2.5 pr-1 text-xs font-medium shadow-sm",
            danger ? "border border-destructive/40 bg-background text-destructive" : "bg-success text-success-foreground",
          )}>
            {!danger && <Check className="h-3 w-3" />}{lbl(v)}
            <button type="button" aria-label={`Remove ${v}`} onClick={() => onRemove(v)} className="rounded-full p-0.5 hover:bg-foreground/10"><X className="h-3 w-3" /></button>
          </span>
        ))}
        {cfg.disabledHint ? (
          <span className="text-xs text-muted-foreground">{cfg.disabledHint}</span>
        ) : open ? (
          <div className="relative">
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKey}
              onBlur={() => setTimeout(close, 120)}
              placeholder={cfg.showOnOpen ? "Pick or search…" : "Start typing…"}
              className="h-7 w-48 rounded-full border border-primary bg-background px-3 text-xs outline-none ring-2 ring-primary/15"
            />
            {(items.length > 0 || (q && !cfg.allowCustom)) && (
              <div className="absolute left-0 top-full z-50 mt-1 min-w-full w-max max-w-xs overflow-hidden rounded-lg border bg-popover p-1 shadow-md">
                {items.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">No matches</div>}
                {items.map((it, i) => (
                  <button
                    key={(it.custom ? "c:" : "") + it.v}
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); pick(it.v); }}
                    onMouseEnter={() => setHi(i)}
                    className={cn("flex w-full items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 py-1.5 text-left text-xs", i === hi ? "bg-accent text-accent-foreground" : "text-foreground")}
                  >
                    {it.custom ? <><Plus className="h-3 w-3" /> Add “{it.v}”</> : <span className="truncate">{lbl(it.v)}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <button type="button" onClick={() => setOpen(true)} className="inline-flex h-7 items-center gap-1 rounded-full border border-dashed px-2.5 text-xs text-muted-foreground hover:border-primary hover:text-primary">
            <Plus className="h-3 w-3" /> Add
          </button>
        )}
      </div>
    </div>
  );
}

/** Describe mode: builds the audience inline with AI on open, then lets the user remove/add chips. */
export function InlineAudienceBuilder({ data, updateData, companyProfileId, campaignKnowledgeId, guestToken, workspaceId, onAiApplied, compact }: Props) {
  const canAi = !!companyProfileId && !!campaignKnowledgeId;
  const base = { workspaceId: guestToken ? null : workspaceId ?? null, guestToken: guestToken ?? null, profileId: companyProfileId, campaignKnowledgeId };
  const q = useAudienceContext(base, campaignKnowledgeId || "");
  const [building, setBuilding] = useState(false);
  const started = useRef(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout>>();

  const values = (k: Key) => ((data as any)[k] ?? []) as string[];
  // Countries may already come from the strategy, so only other filters count as "already built".
  const hasAny = ALL_KEYS.some((k) => k !== "countries" && k !== "cities" && values(k).length > 0);
  const isV2 = canAi && q.data ? !!q.data.isV2 : canAi && !q.isError;

  const persist = (next: Partial<ManagedCampaignData>) => {
    if (!canAi || !isV2) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      const filters: Record<string, string[]> = {};
      ALL_KEYS.forEach((k) => { filters[k] = ((next as any)[k] ?? values(k)) as string[]; });
      call({ ...base, action: "audience_save", filters }).catch(() => { /* silent; local data is the source of truth */ });
    }, 1500);
  };
  useEffect(() => () => clearTimeout(saveTimer.current), []);

  const applyAudience = (a: Record<string, any>) => {
    // The server already limits countries to the strategy's picks; prefer those over what's on screen.
    const picked = Array.isArray(a.countries) && a.countries.length ? (a.countries as string[]) : values("countries");
    const next: Partial<ManagedCampaignData> = {};
    ALL_KEYS.forEach((k) => { (next as any)[k] = Array.isArray(a[k]) ? a[k] : []; });
    // Keep the countries the user picked in the strategy; never let AI swap them for its own places.
    if (picked.length) { next.countries = picked; if (!(Array.isArray(a.cities) && a.cities.length)) next.cities = values("cities").filter(() => !a.countries?.length); }
    updateData(next);
    onAiApplied?.();
    return next;
  };

  const [dlgOpen, setDlgOpen] = useState(false);
  const [preview, setPreview] = useState<Record<string, string[]> | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const build = async () => {
    if (compact) {
      setDlgOpen(true); setPreview(null); setErr(null); setBuilding(true);
      try {
        const r = await call({ ...base, action: "audience_build" });
        if (!r?.audience) throw new Error("No audience returned.");
        const a = r.audience as Record<string, any>;
        const p: Record<string, string[]> = {};
        ALL_KEYS.forEach((k) => { p[k] = Array.isArray(a[k]) ? a[k] : []; });
        if (!p.countries.length) p.countries = values("countries");
        setPreview(p);
      } catch (e: any) {
        setErr(e.message || "Couldn't build the audience.");
      } finally { setBuilding(false); }
      return;
    }
    setBuilding(true);
    try {
      const r = await call({ ...base, action: "audience_build" });
      if (r?.audience) { const next = applyAudience(r.audience); persist(next); }
    } catch (e: any) {
      toast.error(e.message || "Couldn't build the audience. Add chips below.");
    } finally { setBuilding(false); }
  };

  const applyPreview = () => {
    if (!preview) return;
    const next = applyAudience(preview);
    persist(next);
    setDlgOpen(false);
    toast.success("Audience applied");
  };

  // Never auto-build here: reuse the audience prepared when the strategy was saved (or saved later), if it matches.
  const savedMatch = !!q.data?.audience && !!q.data?.strategyKey && q.data.audience.strategy_key === q.data.strategyKey;
  const preparing = !hasAny && !savedMatch && !!q.data?.preparing;
  useEffect(() => {
    if (started.current || !canAi || hasAny) return;
    if ((q.data === undefined || q.isFetching) && !q.isError) return; // wait for fresh context
    if (q.data?.preparing && !savedMatch) return; // still being prepared; keep polling
    started.current = true;
    if (!isV2) return;
    if (savedMatch) applyAudience(q.data!.audience);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data, q.isError, q.isFetching, canAi, hasAny, savedMatch]);

  const change = (k: Key, list: string[]) => { const next = { [k]: list } as Partial<ManagedCampaignData>; updateData(next); persist(next); };
  const add = (k: Key, v: string) => { if (!values(k).some((x) => x.toLowerCase() === v.toLowerCase())) change(k, [...values(k), v]); };
  const remove = (k: Key, v: string) => change(k, values(k).filter((x) => x !== v));

  if ((building && !compact) || preparing) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-2xl border bg-card py-14 shadow-sm">
        <Loader2 className="h-7 w-7 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">{building ? "Building your audience…" : "Preparing your audience…"}</p>
      </div>
    );
  }

  const sizeLabel = (v: string) => COMPANY_SIZES.find((o) => o.value === v)?.label ?? v;
  const senLabel = (v: string) => SENIORITY.find((o) => o.value === v)?.label ?? v;
  const uniq = (a: string[]) => Array.from(new Set(a));
  const countries = values("countries");
  const cityOpts = uniq(countries.flatMap((c) => CITIES_BY_COUNTRY[c] ?? []));
  const deptTitles = uniq(values("departments").flatMap((d) => JOB_TITLES_BY_DEPARTMENT[d] ?? []));
  const senWords = values("seniorityLevels").flatMap((v) => {
    const l = senLabel(v).toLowerCase();
    return [l, ...(v === "c-level" || l.includes("cxo") ? ["chief", "ceo", "cto", "cfo", "coo"] : []), ...(v === "vp" ? ["vice president", "vp"] : []), ...(v === "owner" ? ["founder", "owner"] : [])];
  });
  const senFit = (t: string) => senWords.some((w) => w && t.toLowerCase().includes(w.split(" ")[0]));
  const titleOpts = uniq([...deptTitles.filter(senFit), ...deptTitles, ...ALL_JOB_TITLES.filter(senFit), ...ALL_JOB_TITLES]);
  const indWords = values("industries").flatMap((i) => i.toLowerCase().split(/[\s&,/-]+/).filter((w) => w.length > 3));
  const indRelated = INDUSTRIES.filter((i) => indWords.some((w) => i.toLowerCase().includes(w)));
  const indOpts = uniq([...indRelated, ...INDUSTRIES]);
  const kwIdeas = uniq([...values("industries"), ...values("companyKeywords"), ...values("personKeywords")]);
  const excl = [...values("excludeCompanyKeywords"), ...values("excludePersonKeywords")];
  const cfgFor = (k: Key): GroupCfg => {
    switch (k) {
      case "countries": return { options: COUNTRIES, allowCustom: false };
      case "cities": return countries.length
        ? { options: cityOpts, allowCustom: true, showOnOpen: true }
        : { options: [], allowCustom: false, disabledHint: "Add a country first" };
      case "industries": return { options: indOpts, allowCustom: true, blocked: excl };
      case "companySizes": return { options: COMPANY_SIZES.map((o) => o.value), allowCustom: false, labelFor: sizeLabel, showOnOpen: true };
      case "seniorityLevels": return { options: SENIORITY.map((o) => o.value), allowCustom: false, labelFor: senLabel, showOnOpen: true };
      case "departments": return { options: DEPARTMENTS, allowCustom: false, showOnOpen: true };
      case "jobTitles": return { options: titleOpts, allowCustom: true, blocked: excl };
      case "excludeCompanyKeywords": case "excludePersonKeywords": return { options: ["Student", "Intern", "Freelancer", "Recruiter", "Job seeker", "Retired", "Consultant", "Students"], allowCustom: true, showOnOpen: true };
      default: return { options: kwIdeas, allowCustom: true, blocked: excl };
    }
  };

  const placeOk = values("countries").length > 0 || values("cities").length > 0;

  if (compact) {
    if (!canAi || !isV2) return null;
    const lblFor = (k: Key) => (k === "companySizes" ? sizeLabel : k === "seniorityLevels" ? senLabel : (v: string) => v);
    const groups = preview ? [...GROUPS, ...EXCL].filter(([k]) => (preview[k] ?? []).length > 0) : [];
    const drop = (k: Key, v: string) => setPreview((p) => (p ? { ...p, [k]: p[k].filter((x) => x !== v) } : p));
    return (
      <>
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-card px-4 py-3 shadow-sm">
          <p className="text-sm text-muted-foreground">Fill in the tabs, or let AI do it.</p>
          {hasAny ? (
            <Button variant="outline" size="sm" className="gap-1.5" onClick={build}><RefreshCw className="h-4 w-4" /> Rebuild</Button>
          ) : (
            <Button size="sm" className="gap-1.5" onClick={build}><Sparkles className="h-4 w-4" /> Build audience with AI</Button>
          )}
        </div>
        <Dialog open={dlgOpen} onOpenChange={(o) => { if (!building) setDlgOpen(o); }}>
          <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>Suggested audience</DialogTitle>
              <DialogDescription>Remove anything that doesn't fit, then apply.</DialogDescription>
            </DialogHeader>
            <div className="-mx-6 flex-1 overflow-y-auto px-6">
              {building ? (
                <div className="flex flex-col items-center gap-3 py-14">
                  <Loader2 className="h-7 w-7 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">Building your audience…</p>
                </div>
              ) : err ? (
                <div className="flex flex-col items-center gap-3 py-10 text-center">
                  <p className="text-sm text-destructive">{err}</p>
                  <Button variant="outline" size="sm" onClick={build}>Try again</Button>
                </div>
              ) : preview ? (
                <div className="space-y-4 py-2">
                  {hasAny && <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">These picks will replace your current audience.</p>}
                  {groups.map(([k, label]) => {
                    const danger = k.startsWith("exclude");
                    return (
                      <div key={k} className="space-y-1.5">
                        <div className={cn("flex items-center gap-1 text-xs font-semibold uppercase tracking-wide", danger ? "text-destructive" : "text-muted-foreground")}>
                          {danger && <Ban className="h-3 w-3" />}{label} · {preview[k].length}
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {preview[k].map((v) => (
                            <span key={v} className={cn("inline-flex items-center gap-1 rounded-full py-1 pl-2.5 pr-1 text-xs font-medium shadow-sm", danger ? "border border-destructive/40 bg-background text-destructive" : "bg-success text-success-foreground")}>
                              {!danger && <Check className="h-3 w-3" />}{lblFor(k)(v)}
                              {k !== "countries" && (
                                <button type="button" aria-label={`Remove ${v}`} onClick={() => drop(k, v)} className="rounded-full p-0.5 hover:bg-foreground/10"><X className="h-3 w-3" /></button>
                              )}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDlgOpen(false)} disabled={building}>Cancel</Button>
              <Button onClick={applyPreview} disabled={building || !preview}>Apply to audience</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <div className="space-y-4 rounded-2xl border bg-card p-5 shadow-sm sm:p-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-foreground">Your audience</h3>
          <p className="text-sm text-muted-foreground">Remove anything that doesn't fit, or add your own.</p>
        </div>
        {canAi && isV2 && (
          hasAny ? (
            <Button variant="outline" size="sm" className="gap-1.5" onClick={build}>
              <RefreshCw className="h-4 w-4" /> Rebuild
            </Button>
          ) : (
            <Button size="sm" className="gap-1.5" onClick={build}>
              <Sparkles className="h-4 w-4" /> Build audience with AI
            </Button>
          )
        )}
      </div>
      {!placeOk && <p className="text-xs text-destructive">Add at least one country or city.</p>}
      <div className="grid gap-5 sm:grid-cols-2">
        {GROUPS.map(([k, label]) => (
          <ChipGroup key={k} cfg={cfgFor(k)} label={label} values={values(k)} onAdd={(v) => add(k, v)} onRemove={(v) => remove(k, v)} />
        ))}
      </div>
      <div className="grid gap-5 rounded-lg border border-destructive/20 bg-destructive/5 p-3 sm:grid-cols-2">
        {EXCL.map(([k, label]) => (
          <ChipGroup key={k} cfg={cfgFor(k)} danger label={label} values={values(k)} onAdd={(v) => add(k, v)} onRemove={(v) => remove(k, v)} />
        ))}
      </div>
    </div>
  );
}
